"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

export interface ModalConfig {
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  content: ReactNode;
  actions?: ReactNode;
}

export interface ModalState {
  isOpen: boolean;
  config: ModalConfig | null;
}

interface ModalContextType {
  modal: ModalState;
  openModal: (config: ModalConfig) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    config: null,
  });

  const openModal = (config: ModalConfig) => {
    setModal({
      isOpen: true,
      config,
    });
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      config: null,
    });
  };

  return <ModalContext.Provider value={{ modal, openModal, closeModal }}>{children}</ModalContext.Provider>;
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
